import {useQuery} from "@apollo/client";
import classNames from "classnames";
import {parseISO} from "date-fns";
import {useRef, useState} from "react";

import {usePublicUserContext} from "@/components/context/hooks.ts";
import {useDateRange} from "@/components/datePicker/DatePickerURLRangeParamsContext.ts";
import {Last24Hours} from "@/components/datePicker/subComponents/DatePickerShortcutList/DatePickerShortcutList.utils.ts";
import {LoadingContainer} from "@/components/placeholder/LoadingContainer.tsx";
import {Redirect} from "@/components/router/Redirect.tsx";
import {GQL_IS_IT_DOWN_SERVICE_QUERY} from "@/lib/queries/isitdown.ts";
import {ResponseStatus} from "@/lib/types/common.ts";
import {IsItDownServiceResult, IsItDownVariant, PublicProfile, PublicProfileStats} from "@/lib/types/isitdown.ts";
import {CTABlock} from "@/marketingPages/components/ctablock/CTABlock.tsx";
import {TopReportedIssues} from "@/marketingPages/components/topreportedissues/TopReportedIssues.tsx";
import {AllServicesBanner} from "@/marketingPages/isitdown/components/allservicesbanner/AllServicesBanner.tsx";
import {ComponentStatus} from "@/marketingPages/isitdown/components/componentstatus/ComponentStatus.tsx";
import {DowntimeNotifications} from "@/marketingPages/isitdown/components/downtimenotifications/DowntimeNotifications.tsx";
import {IncidentHistory} from "@/marketingPages/isitdown/components/incidenthistory/IncidentHistory.tsx";
import {IsItDownDetailsHeader} from "@/marketingPages/isitdown/components/isitdowndetailsheader/IsItDownDetailsHeader.tsx";
import {IsItDownFAQs} from "@/marketingPages/isitdown/components/isitdownfaqs/IsItDownFAQs.tsx";
import {IsItDownReviews} from "@/marketingPages/isitdown/components/isitdownreviews/IsItDownReviews.tsx";
import {PublicProfilesResponseTime} from "@/marketingPages/isitdown/components/responsetime/PublicProfilesResponseTime.tsx";
import {PublicProfilesServiceHealth} from "@/marketingPages/isitdown/components/servicehealth/PublicProfilesServiceHealth.tsx";
import {StatusPageBanner} from "@/marketingPages/isitdown/components/statuspagebanner/StatusPageBanner.tsx";
import {ThirdPartyAccordion} from "@/marketingPages/isitdown/components/thirdpartyaccordion/ThirdPartyAccordion.tsx";
import {ThirdPartyTabs} from "@/marketingPages/isitdown/components/thirdpartytabs/ThirdPartyTabs.tsx";
import {ALERTS_FIXED_RANGE_KEY, ALERTS_RANGE_KEY_FROM, ALERTS_RANGE_KEY_TO} from "@/pages/alerting/AlertsPage.tsx";

import styles from "./IsItDownDetailsBody.module.scss";

interface IsItDownDetailsBodyProps {
  slug: string;
}

export const IsItDownDetailsBody = ({slug}: IsItDownDetailsBodyProps) => {
  const downReportRef = useRef<HTMLDivElement | null>(null);
  const [showDownReportForm, setShowDownReportForm] = useState(false);
  const [showDownReportSuccess, setShowDownReportSuccess] = useState(false);

  const {timezone} = usePublicUserContext();
  const {dateRange} = useDateRange({
    fromKey: ALERTS_RANGE_KEY_FROM,
    toKey: ALERTS_RANGE_KEY_TO,
    fixedRangeKey: ALERTS_FIXED_RANGE_KEY,
    withDefault: Last24Hours,
    timezone: timezone,
  });

  const {data, previousData} = useQuery<IsItDownServiceResult>(GQL_IS_IT_DOWN_SERVICE_QUERY, {
    variables: {
      slug: slug,
    },
  });

  const renderData = data || previousData;
  if (!renderData) {
    return <LoadingContainer className="py-5 mb-5" />;
  }
  const {service, stats, categoryStats, incidents, components, similarServices} = renderData;

  if ("status" in service && service.status === ResponseStatus.ERROR) {
    return <Redirect url="/status" />;
  }
  const publicProfile = service as PublicProfile;
  const publicProfileName = publicProfile.name || publicProfile.slug;
  const publicProfileStats = stats as PublicProfileStats;

  const openDownReportForm = () => {
    setShowDownReportForm(true);
    setShowDownReportSuccess(false);
    downReportRef.current?.scrollIntoView({behavior: "smooth"});
  };

  return (
    <div>
      <IsItDownDetailsHeader
        profile={publicProfile}
        openDownReportForm={openDownReportForm}
        lastCheckTimestamp={parseISO(publicProfileStats.last_check_timestamp)}
        reportCount={categoryStats.items.reduce((acc, {count}) => acc + count, 0)}
        similarServices={similarServices}
      />
      <div className={styles.detailsContainer}>
        <div className={classNames(styles.topSection, styles[publicProfile.variant.toLowerCase()])}>
          <PublicProfilesServiceHealth
            profile={publicProfile}
            stats={publicProfileStats}
            timestamps={categoryStats.timestamps}
            start={dateRange.from}
            end={dateRange.to}
            timezone={timezone}
            variant={publicProfile.variant}
          />
          <TopReportedIssues
            categories={categoryStats.items}
            profileId={publicProfile.id}
            downReportRef={downReportRef}
            showForm={showDownReportForm}
            setShowForm={setShowDownReportForm}
            showSuccess={showDownReportSuccess}
            setShowSuccess={setShowDownReportSuccess}
          />
        </div>
        {publicProfile.variant !== IsItDownVariant.CLOUDSTATUS && (
          <PublicProfilesResponseTime stats={publicProfileStats} start={dateRange.from} end={dateRange.to} />
        )}
        <AllServicesBanner />
        {publicProfile.variant !== IsItDownVariant.CHECKS && (
          <>{incidents.items.length > 0 && <IncidentHistory incidents={incidents.items} />}</>
        )}
      </div>
      <DowntimeNotifications profile={publicProfile} />

      {publicProfile.variant === IsItDownVariant.CHECKS ? (
        <IsItDownFAQs serviceName={publicProfileName} />
      ) : (
        <div className={styles.detailsContainer}>
          <>{components.items.length > 0 && <ComponentStatus components={components.items} />}</>
          {publicProfile.variant === IsItDownVariant.HYBRID && <StatusPageBanner serviceName={publicProfileName} />}
          <ThirdPartyTabs profile={publicProfile} />
          <ThirdPartyAccordion serviceName={publicProfileName} />
        </div>
      )}
      {publicProfile.variant !== IsItDownVariant.CLOUDSTATUS && <IsItDownReviews />}
      {publicProfile.variant === IsItDownVariant.HYBRID && <IsItDownFAQs serviceName={publicProfileName} />}
      <CTABlock title="Start monitoring your website in seconds!" topBar bottomBar />
    </div>
  );
};
